/**
Author - Brijesh Pandey
Git - https://github.com/Brijesh-Pandey
**/

import React from 'react';
import styled from 'styled-components';
import parse from 'html-react-parser';

import {ActionButton, Spinner, Error} from '../../../commons';
import IMAGES from 'constants/Images';

const DeclarationList = ({
  isLoading,
  isError,
  declarationsList,
  handleContinueClick,
  skipBrokingFlow,
}) => {
  const convertMarkdownToHtml = (text) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  const content = declarationsList.allowedIncomeDeclarations?.map((eachDeclaration) => (
    <ContentWrapper>
      <BulletImage
        alt="bullet-image"
        src="https://i.wlycdn.com/wealthy-home-page/bullet-star.svg"
        loading="lazy"
      />
      <DeclarationContent>{parse(eachDeclaration.render)}</DeclarationContent>
    </ContentWrapper>
  ));

  return (
    <>
      {isError ? (
        <ErrorContainer>
          <Error />
        </ErrorContainer>
      ) : isLoading ? (
        <LoaderContainer>
          <Spinner color="blue" message="Fetching Details" />
        </LoaderContainer>
      ) : (
        <DeclarationsWrapper>
          <>
            <Title>{declarationsList.pageTitle}</Title>
            <ContentContainer>
              {declarationsList.infoNotice && (
                <InfoNotice>
                  <InfoIcon
                    src={IMAGES.infoIconImage}
                    alt="info"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <InfoText>{parse(convertMarkdownToHtml(declarationsList.infoNotice))}</InfoText>
                </InfoNotice>
              )}
              <CardTitle>{declarationsList.cardTitle}</CardTitle>
              {content}
              <ActionButton
                className="fno-declaration-button"
                buttonText="Continue"
                callback={handleContinueClick}
              />
              <SkipButton onClick={skipBrokingFlow}>Skip for now</SkipButton>
            </ContentContainer>
          </>
        </DeclarationsWrapper>
      )}
    </>
  );
};

const LoaderContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentContainer = styled.div`
  background: #fff;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ErrorContainer = styled.div`
  background: #ffffff;
  height: 100vh;
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: scroll;
  padding: 2.4rem;
  text-align: center;

  .error-image {
    width: 60%;
    margin-bottom: 5rem;
  }

  @media (max-width: 768px) {
    background: #fff;
    .error-image {
      width: 90%;
    }
  }
`;

const DeclarationsWrapper = styled.div`
  height: 100%;
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: scroll;

  @media (max-width: 768px) {
    background: #fff;
  }

  .fno-declaration-button {
    margin-top: 2rem;
  }
`;

const Title = styled.h1.attrs({
  className: 'body-medium',
})`
  @media (max-width: 768px) {
    border-bottom: none;
  }
  border-bottom: 1px solid #e0e0e0;
  padding: 1.5rem;
  margin: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;

  li {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    padding-right: 16px;

    strong {
      display: inline;
      font-weight: 600;
    }
  }
`;

const SkipButton = styled.button.attrs({
  className: 'button-title',
})`
  background: transparent;
  color: #6725f4;
  border-radius: 52px !important;
  width: 100%;
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    background: transparent;
  }
`;

const CardTitle = styled.p`
  text-align: left;
  color: #000000;
  margin-bottom: -1.5rem;
  margin-top: 1.5rem;
`;

const BulletImage = styled.img`
  margin-right: 1rem;
  height: 100%;
  margin-top: 2.8rem;
  padding-bottom: 0.5rem;
`;

const DeclarationContent = styled.p.attrs({
  className: 'body-small',
})`
  margin: 2.4rem 0 0 0;
  padding: 0;
  color: #7e7e7e;
`;

const InfoNotice = styled.div`
  background: #e8f4fd;
  border-left: 4px solid #6725f4;
  border-radius: 4px;
  padding: 1rem;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem 0;
  }
`;

const InfoIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const InfoText = styled.div`
  margin: 0;
  padding: 0;
  color: #1565c0;
  font-size: 14px;
  text-align: left;

  strong {
    font-weight: 600;
    color: #0d47a1;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export default DeclarationList;
